import React, { useContext, useEffect, useRef, useState } from "react";

import { useParams, useHistory } from "react-router-dom";

import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import ChatList from "./ChatList";
import ChatMessages from "./ChatMessages";
import { UsersFilter } from "../../components/UsersFilter";
import api from "../../services/api";
import useResponsive from "../../utils/useResponsive";
import { socketConnection } from "../../services/socket";
import { has, isObject } from "lodash";
import { getBackendUrl } from "../../config";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { toast } from "react-hot-toast";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import GroupAddOutlinedIcon from "@material-ui/icons/GroupAddOutlined";
import PhotoCameraOutlinedIcon from "@material-ui/icons/PhotoCameraOutlined";
import { getTokens } from "../../styles/tokens";
import { getWa } from "./chatTheme";

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
const withWidth = () => (WrappedComponent) => (props) =>
  <WrappedComponent {...props} width="xs" />;

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  const wa = getWa(theme.palette.type === "dark");
  return {
    // tela cheia, como a central de atendimento: sem moldura em volta da conversa
    mainContainer: {
      display: "flex",
      flexDirection: "column",
      position: "relative",
      flex: 1,
      minHeight: 0,
      padding: 0,
      height: "calc(100% - 64px)",
      overflow: "hidden",
      backgroundColor: tk.paper,
      borderTop: `1px solid ${tk.line}`,
      boxSizing: "border-box",
      [theme.breakpoints.down("sm")]: { height: "calc(100% - 56px)" },
    },
    layout: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      height: "100%",
      overflow: "hidden",
    },
    listPane: {
      flex: "none",
      width: 390,
      minWidth: 0,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      backgroundColor: tk.paper,
      borderRight: `1px solid ${tk.line}`,
      [theme.breakpoints.down("md")]: { width: 340 },
    },
    // !important: a regra de largura por breakpoint do listPane sai depois no CSS
    listPaneFull: {
      width: "100% !important",
      borderRight: 0,
    },
    messagePane: {
      flex: 1,
      minWidth: 0,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    },
    listHeader: {
      flex: "none",
      minHeight: 30,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "14px 16px 10px",
    },
    listTitle: {
      margin: 0,
      color: tk.text,
      fontSize: 22,
      fontWeight: 700,
      lineHeight: "28px",
      letterSpacing: "-0.02em",
    },
    headerBtn: {
      flex: "none",
      width: 36,
      height: 36,
      padding: 0,
      borderRadius: "50%",
      color: wa.chipText,
      "& svg": { fontSize: 21 },
      "&:hover": { backgroundColor: wa.chip },
    },
    // "Chats · Grupos" em pílulas, como os filtros da lista de conversas
    pillRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "2px 16px 10px",
      borderBottom: `1px solid ${tk.line}`,
    },
    pill: {
      flex: "none",
      height: 32,
      padding: "0 13px",
      border: 0,
      borderRadius: 16,
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 14,
      color: wa.chipText,
      backgroundColor: wa.chip,
      transition: "color 150ms ease, background-color 150ms ease",
      "&:focus-visible": {
        outline: `2px solid ${wa.accent}`,
        outlineOffset: 1,
      },
    },
    pillOn: {
      color: wa.chipOnText,
      backgroundColor: wa.chipOn,
      fontWeight: 500,
    },
    // estado vazio no padrão do WhatsApp Web
    emptyConversation: {
      display: "flex",
      flex: 1,
      minHeight: 0,
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: 32,
      color: tk.textSecondary,
      textAlign: "center",
      backgroundColor: wa.emptyBg,
      "& svg": {
        fontSize: 64,
        marginBottom: 8,
        color: tk.textMuted,
      },
      "& strong": { color: tk.text, fontSize: 22, fontWeight: 400 },
      "& span": { maxWidth: 380, fontSize: 14, lineHeight: 1.5 },
    },
    // ---------- modais (mesmo padrão do "Nova conversa" do atendimento) ----------
    dialogPaper: {
      width: "min(440px, calc(100vw - 32px))",
      maxWidth: "none",
      margin: 16,
      borderRadius: 12,
    },
    dialogPaperWide: {
      width: "min(560px, calc(100vw - 32px))",
    },
    dialogHeader: {
      padding: "18px 22px 6px",
    },
    dialogTitle: {
      margin: 0,
      fontSize: 19,
      fontWeight: 700,
      lineHeight: "26px",
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary,
    },
    dialogSubtitle: {
      margin: "2px 0 0",
      fontSize: 13,
      lineHeight: "18px",
      color: theme.palette.text.secondary,
    },
    dialogContent: {
      padding: "12px 22px 6px",
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },
    dialogActions: {
      padding: "12px 22px 18px",
      gap: 6,
    },
    dialogCancel: {
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    dialogConfirm: {
      fontWeight: 600,
      minWidth: 120,
      borderRadius: 20,
      boxShadow: "none",
    },
    dialogDanger: {
      fontWeight: 600,
      minWidth: 120,
      borderRadius: 20,
      boxShadow: "none",
      color: "#ffffff",
      background: tk.semantic.error,
      backgroundColor: tk.semantic.error,
      "&:hover": {
        background: tk.semantic.error,
        backgroundColor: tk.semantic.error,
        filter: "brightness(0.93)",
      },
    },
    field: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      minWidth: 0,
    },
    fieldLabel: {
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    usersField: {
      // o UsersFilter traz um recuo próprio; aqui ele alinha com os outros campos
      "& > .MuiBox-root": { padding: "0 !important" },
    },
    photoRow: {
      display: "flex",
      alignItems: "center",
      gap: 14,
    },
    photoAvatar: {
      width: 64,
      height: 64,
      "& svg": { fontSize: 26 },
    },
    placeholder: {
      color: theme.palette.text.disabled,
    },
    dialogText: {
      margin: 0,
      color: tk.text,
      fontSize: 14,
      lineHeight: 1.5,
    },
  };
});

export function ChatModal({
  open,
  chat,
  type,
  handleClose,
  handleLoadNewChat,
  findChats,
  setChats,
  setChatsPageInfo,
  chats,
  loggedInUserId,
}) {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [groupImage, setGroupImage] = useState("");
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (open) {
      setTitle("");
      setUsers([]);
      setDescription("");
      setGroupImage("");
      setImageFile(null);
      if (type === "edit" && chat && chat.users) {
        const userList = chat.users.map((u) => ({
          id: u.user.id,
          name: u.user.name,
        }));
        setUsers(userList);
        setTitle(chat.title || "");
        setDescription(chat.description || "");
        if (chat.groupImage) {
          setGroupImage(chat.groupImage);
        }
      }
    }
  }, [chat, open, type]);

  // const handleImageChange = (e) => {
  //   if (e.target.files && e.target.files[0]) {
  //     setImageFile(e.target.files[0]);
  //     setGroupImage(URL.createObjectURL(e.target.files[0]));
  //   }
  // };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const previewUrl = URL.createObjectURL(file);
      setImageFile(file);
      setGroupImage(previewUrl);
      console.log("Preview URL:", previewUrl);
    }
  };

  const handleSave = async () => {
    try {
      let uploadedImageUrl = groupImage;
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        const uploadRes = await api.post("/chats/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploadedImageUrl = uploadRes.data.url;
      }

      if (type === "edit" && chat) {
        const { data } = await api.put(`/chats/${chat.id}`, {
          users,
          title,
          description,
          groupImage: uploadedImageUrl,
        });
        handleLoadNewChat(data);
      } else {
        const isCreatingGroup = users.length > 1 || title !== "";
        console.log("isCreatingGroup:", isCreatingGroup);

        if (!isCreatingGroup) {
          const selectedUserId = users[0]?.id;
          console.log("selectedUserId:", selectedUserId);
          console.log("loggedInUserId:", loggedInUserId);
          console.log("chats available:", chats);

          const existingChat = chats.find((c) => {
            const hasSelectedUser = c.users.some(
              (u) => u.userId === selectedUserId
            );
            const hasLoggedInUser = c.users.some(
              (u) => u.userId === loggedInUserId
            );
            const isIndividualChat = !c.isGroup && c.users.length === 2;

            console.log(
              `Checking chat ${c.id}: isIndividualChat=${isIndividualChat}, hasSelectedUser=${hasSelectedUser}, hasLoggedInUser=${hasLoggedInUser}`
            );

            return isIndividualChat && hasSelectedUser && hasLoggedInUser;
          });

          console.log("Found existingChat:", existingChat);

          if (existingChat) {
            toast.info("Chat com este usuário já existe!");
            handleLoadNewChat(existingChat);
            handleClose();
            return;
          }
        }

        const { data } = await api.post("/chats", {
          users,
          title,
          description,
          groupImage: uploadedImageUrl,
          isGroup: isCreatingGroup,
        });
        handleLoadNewChat(data);
      }
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao criar/editar chat");
    }
  };

  const groupImageSrc =
    typeof groupImage === "string"
      ? groupImage.startsWith("http") || groupImage.startsWith("blob:")
        ? groupImage
        : groupImage
        ? `${getBackendUrl()}${groupImage}`
        : undefined
      : URL.createObjectURL(groupImage);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.dialogHeader}>
        <h2 id="alert-dialog-title" className={classes.dialogTitle}>
          {type === "edit"
            ? i18n.t("chatIndex.modal.editTitle") || "Editar Grupo"
            : i18n.t("chatIndex.modal.title") || "Criar Grupo"}
        </h2>
        {/* TODO i18n */}
        <p className={classes.dialogSubtitle}>
          Dê um nome ao grupo e escolha quem participa.
        </p>
      </div>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.photoRow}>
          <Avatar src={groupImageSrc} className={classes.photoAvatar}>
            <PhotoCameraOutlinedIcon />
          </Avatar>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="group-image-upload"
            type="file"
            onChange={handleImageChange}
          />
          <label htmlFor="group-image-upload">
            <Button variant="outlined" size="small" component="span">
              {groupImage ? "Alterar Foto do Grupo" : "Adicionar Foto do Grupo"}
            </Button>
          </label>
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Título</span>
          <TextField
            placeholder="Título"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ "aria-label": "Título" }}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Descrição</span>
          <TextField
            placeholder="Descrição do grupo"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
            inputProps={{ "aria-label": "Descrição" }}
          />
        </div>
        <div className={`${classes.field} ${classes.usersField}`}>
          <span className={classes.fieldLabel}>Participantes</span>
          <UsersFilter
            onFiltered={(users) => setUsers(users)}
            initialUsers={users}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} className={classes.dialogCancel}>
          {i18n.t("chatIndex.modal.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          className={classes.dialogConfirm}
          disabled={
            users === undefined ||
            users.length === 0 ||
            title === null ||
            title === "" ||
            title === undefined
          }
        >
          {i18n.t("chatIndex.modal.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Chat(props) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState("new");
  const [currentChat, setCurrentChat] = useState({});
  const [chats, setChats] = useState([]);
  const [chatsPageInfo, setChatsPageInfo] = useState({ hasMore: false });
  const [messages, setMessages] = useState([]);
  const [messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tab, setTab] = useState(0);
  const [showGroupChats, setShowGroupChats] = useState(false);
  const isMounted = useRef(true);
  const scrollToBottomRef = useRef();
  const messageListRef = useRef();
  const { id } = useParams();
  const isMdUp = useResponsive();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [forwardModalOpen, setForwardModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editText, setEditText] = useState("");
  const [selectedChatForForward, setSelectedChatForForward] = useState(null);
  const [justOpenedChat, setJustOpenedChat] = useState(false);

  // Definir socket e companyId no escopo da função
  const companyId = user?.companyId;
  const socket = companyId
    ? socketConnection({ companyId, userId: user?.id })
    : null;

  // Monitorar mudanças no estado chats
  useEffect(() => {
    const groups = chats.filter(
      (chat) => chat.isGroup === true || chat.isGroup === "true"
    );
    const individualChats = chats.filter(
      (chat) => !(chat.isGroup === true || chat.isGroup === "true")
    );
  }, [chats, showGroupChats]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current && user?.id) {
      findChats()
        .then((data) => {
          const { records } = data;
          console.log("Chats encontrados:", records.length);
          if (isMounted.current && records.length > 0) {
            setChats(records);
            setChatsPageInfo(data);

            if (id && records.length) {
              const chat = records.find((r) => r.uuid === id);
              if (chat) {
                selectChat(
                  chat,
                  "useEffect[user?.id] (carregar chats iniciais)"
                );
              }
            }
          } else {
            console.log("Nenhum chat encontrado ou componente desmontado");
          }
        })
        .catch((err) => {
          console.error("Erro ao carregar chats iniciais:", err);
        });
    } else {
      console.log("Componente não montado ou usuário não disponível");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user.id) return;

    const onChatUser = (data) => {
      if (isMounted.current) {
        if (data.action === "create") {
          setChats((prev) => [data.record, ...prev]);
        }
        if (data.action === "update") {
          setChats((prev) =>
            prev.map((chat) =>
              chat.id === data.record.id ? { ...chat, ...data.record } : chat
            )
          );
          if (currentChat && currentChat.id === data.record.id) {
            setCurrentChat(data.record);
          }
        }
      }
    };
    const onChat = (data) => {
      if (!isMounted.current) return;
      if (isMounted.current) {
        if (data.action === "delete") {
          setChats((prev) => prev.filter((c) => c.id !== +data.id));
          setMessages([]);
          setMessagesPage(1);
          setMessagesPageInfo({ hasMore: false });
          setCurrentChat({});
          history.push("/chats");
          setTab(0);
        }
        if (data.action === "new-message" || data.action === "update") {
          // Só atualize se NÃO for o chat atualmente aberto
          if (!currentChat || currentChat.id !== data.chat.id) {
            setChats((prev) =>
              prev.map((chat) =>
                chat.id === data.chat.id ? { ...chat, ...data.chat } : chat
              )
            );
          }
        }
      }
    };

    if (socket && companyId) {
      socket.on(`company-${companyId}-chat-user-${user.id}`, onChatUser);
      socket.on(`company-${companyId}-chat`, onChat);

      return () => {
        socket.off(`company-${companyId}-chat-user-${user.id}`, onChatUser);
        socket.off(`company-${companyId}-chat`, onChat);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, socket, companyId]);

  useEffect(() => {
    if (
      isObject(currentChat) &&
      has(currentChat, "id") &&
      socket &&
      companyId
    ) {
      const onCurrentChat = (data) => {
        if (isMounted.current) {
          if (data.action === "new-message") {
            setMessages((prev) => {
              // Remove mensagem otimista (temp) com mesmo conteúdo e usuário
              const filtered = prev.filter(
                (msg) =>
                  !(
                    msg.id?.toString().startsWith("temp-") &&
                    msg.senderId === data.newMessage.senderId &&
                    msg.message === data.newMessage.message
                  )
              );
              // Evita duplicidade da real
              if (filtered.some((msg) => msg.id === data.newMessage.id)) {
                return filtered;
              }
              return [...filtered, data.newMessage];
            });
            const changedChats = chats.map((chat) => {
              if (chat.id === data.newMessage.chatId) {
                return {
                  ...chat,
                  ...data.chat,
                  lastMessage: data.newMessage,
                  updatedAt: data.newMessage.createdAt,
                };
              }
              return chat;
            });
            setChats(changedChats);
            if (typeof scrollToBottomRef.current === "function") {
              setTimeout(() => {
                scrollToBottomRef.current();
              }, 300);
            }
            // NÃO altere setMessagesPage aqui!
          } else if (data.action === "edit-message") {
            setMessages((prevMessages) =>
              prevMessages.map((msg) =>
                msg.id === data.message.id ? data.message : msg
              )
            );
          } else if (data.action === "delete-message") {
            setMessages((prevMessages) =>
              prevMessages.map((msg) =>
                msg.id === data.message.id ? data.message : msg
              )
            );
          }

          if (data.action === "update") {
            const changedChats = chats.map((chat) => {
              if (chat.id === data.chat.id) {
                return {
                  ...chat,
                  ...data.chat,
                };
              }
              return chat;
            });
            setChats(changedChats);
            if (typeof scrollToBottomRef.current === "function") {
              scrollToBottomRef.current();
            }
          }
        }
      };

      socket.on(`company-${companyId}-chat-${currentChat.id}`, onCurrentChat);

      return () => {
        socket.off(
          `company-${companyId}-chat-${currentChat.id}`,
          onCurrentChat
        );
      };
    }
  }, [currentChat, chats, companyId, socket]);

  // Modifique selectChat para buscar apenas a primeira página ao abrir o chat
  const selectChat = async (chat, reason = "manual") => {
    console.log(`[selectChat] Motivo: ${reason}`, chat);
    if (!isMounted.current) return;
    setMessages([]);
    setMessagesPage(1);
    setMessagesPageInfo(null);
    setJustOpenedChat(true);

    try {
      // Buscar chat atualizado do backend usando UUID
      const { data } = await api.get(`/chats/${chat.uuid}`);
      if (!isMounted.current) return;
      setCurrentChat(data);

      // Buscar apenas a primeira página de mensagens
      await findMessages(data.id, false, 1); // Passa página 1 explicitamente
    } catch (err) {
      if (!isMounted.current) return;
      setCurrentChat(chat);
      await findMessages(chat.id, false, 1);
    }
  };

  // No sendMessage, garanta que NENHUM findMessages, selectChat ou loadMoreMessages é chamado após o envio.
  const sendMessage = async (contentMessage) => {
    if (!currentChat || !currentChat.id) {
      console.error("Nenhum chat selecionado para enviar mensagem");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/chats/${currentChat.id}/messages`, {
        message: contentMessage,
      });
      // Não chame findMessages, selectChat ou loadMoreMessages aqui!
      // Apenas limpe o campo de input, a mensagem será adicionada pelo socket
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
    }
    setLoading(false);
  };

  const deleteChat = async (chat) => {
    try {
      await api.delete(`/chats/${chat.id}`);
    } catch (err) {}
  };

  // Modifique findMessages para aceitar página explicitamente
  const findMessages = async (chatId, isLoadMore = false, page = null) => {
    if (!isMounted.current || !chatId) return;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    let currentScrollHeight = 0;
    let currentScrollTop = 0;
    if (isLoadMore) {
      const messageListElement = document.querySelector(".messageList");
      currentScrollHeight = messageListElement?.scrollHeight || 0;
      currentScrollTop = messageListElement?.scrollTop || 0;
    }

    try {
      const pageToFetch = page !== null ? page : messagesPage;
      const url = `/chats/${chatId}/messages?pageNumber=${pageToFetch}`;
      const { data } = await api.get(url);
      if (!isMounted.current) return;

      if (isMounted.current) {
        const isFirstPage = pageToFetch === 1;
        if (isLoadMore) {
          setMessagesPage((prev) => prev + 1);
        }
        setMessagesPageInfo(data);
        if (isFirstPage) {
          setMessages(data.records);
        } else {
          setMessages((prev) => [...data.records, ...prev]);
          if (isLoadMore) {
            setTimeout(() => {
              if (!isMounted.current) return;
              const messageListElement = document.querySelector(".messageList");
              if (messageListElement) {
                const newScrollHeight = messageListElement.scrollHeight;
                const scrollDifference = newScrollHeight - currentScrollHeight;
                messageListElement.scrollTop =
                  currentScrollTop + scrollDifference;
              }
            }, 100);
          }
        }
      }
    } catch (err) {
      if (!isMounted.current) return;
      console.error("Erro ao carregar mensagens:", err);
    } finally {
      if (isMounted.current) {
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    }
  };

  const loadMoreMessages = async () => {
    if (
      !loadingMore &&
      currentChat &&
      currentChat.id &&
      messagesPageInfo?.hasMore
    ) {
      await findMessages(currentChat.id, true); // Não passe page aqui!
    }
  };

  const findChats = async () => {
    if (!isMounted.current) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/chats`);

      // Contar grupos e chats individuais
      const groups = data.records.filter(
        (chat) => chat.isGroup === true || chat.isGroup === "true"
      );
      const individualChats = data.records.filter(
        (chat) => !(chat.isGroup === true || chat.isGroup === "true")
      );

      if (isMounted.current) {
        setChats(data.records);
        setChatsPageInfo(data);
        console.log("Chats definidos no estado");
      }
      return data;
    } catch (err) {
      console.log("Erro ao carregar chats:", err);
      return { records: [], hasMore: false };
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleLoadNewChat = (data) => {
    if (!isMounted.current) return;

    // Limpar estado anterior
    setMessages([]);
    setMessagesPage(1);
    setMessagesPageInfo(null);
    setCurrentChat(data);
    setJustOpenedChat(true); // Ativar scroll automático

    // No mobile, não mudar a tab automaticamente
    // Deixar o usuário escolher qual tab usar

    history.push(`/chats/${data.uuid}`);

    // Não chame findMessages aqui, selectChat já faz isso
    selectChat(data, "handleLoadNewChat (novo chat criado/editado)");
    // Atualiza a lista de chats
    findChats().then((chatsData) => {
      if (isMounted.current && chatsData && chatsData.records) {
        setChats(chatsData.records);
        setChatsPageInfo(chatsData);
      }
    });
  };

  const handleEditMessage = (message) => {
    // Regra: só pode editar em até 10 minutos
    if (new Date() - new Date(message.createdAt) > 10 * 60 * 1000) {
      alert("Só é possível editar mensagens enviadas há menos de 10 minutos.");
      return;
    }
    setSelectedMessage(message);
    setEditText(message.message);
    setEditModalOpen(true);
  };

  const handleDeleteMessage = (message) => {
    setSelectedMessage(message);
    setDeleteModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const { data } = await api.put(`/chats/messages/${selectedMessage.id}`, {
        message: editText,
      });
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === selectedMessage.id ? { ...msg, message: editText } : msg
        )
      );
      setEditModalOpen(false);
      setSelectedMessage(null);
      setEditText("");
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteSubmit = async () => {
    try {
      await api.delete(`/chats/messages/${selectedMessage.id}`);
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === selectedMessage.id
            ? {
                ...msg,
                isDeleted: true,
                message: "Esta mensagem foi apagada",
                mediaPath: null,
                forwardedFrom: null,
                replyTo: null,
              }
            : msg
        )
      );
      setDeleteModalOpen(false);
      setSelectedMessage(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleForwardMessage = (message) => {
    setSelectedMessage(message);
    setForwardModalOpen(true);
  };

  const handleForwardSubmit = async () => {
    try {
      await api.post(`/chats/messages/${selectedMessage.id}/forward`, {
        targetChatId: selectedChatForForward,
      });
      setForwardModalOpen(false);
      setSelectedMessage(null);
      setSelectedChatForForward(null);
    } catch (err) {
      console.error(err);
    }
  };

  const openNewGroup = () => {
    setDialogType("new");
    setShowDialog(true);
  };

  // Painel da lista: título + criar grupo, busca, pílulas Chats/Grupos e as conversas.
  const renderListPane = (showGroups, onChangeGroups, full) => {
    const filteredChats = chats
      .filter((chat) => {
        const isGroup = chat.isGroup === true || chat.isGroup === "true";
        const shouldShow = showGroups ? isGroup : !isGroup;

        return shouldShow;
      })
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

    return (
      <div
        className={[classes.listPane, full ? classes.listPaneFull : ""].join(
          " "
        )}
      >
        <div className={classes.listHeader}>
          {/* TODO i18n */}
          <h1 className={classes.listTitle}>Chat interno</h1>
          <Tooltip title={i18n.t("chatIndex.createGroup") || "Criar grupo"}>
            <IconButton
              onClick={openNewGroup}
              aria-label={i18n.t("chatIndex.createGroup") || "Criar grupo"}
              className={classes.headerBtn}
            >
              <GroupAddOutlinedIcon />
            </IconButton>
          </Tooltip>
        </div>
        <ChatList
          chats={filteredChats}
          pageInfo={chatsPageInfo}
          loading={loading}
          showingGroups={showGroups}
          handleSelectChat={(chat) =>
            selectChat(chat, "seleção manual na lista")
          }
          handleDeleteChat={(chat) => deleteChat(chat)}
          handleEditChat={(chat) => {
            setCurrentChat(chat);
            setDialogType("edit");
            setShowDialog(true);
          }}
          findChats={findChats}
          toolbar={
            <div className={classes.pillRow} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={!showGroups}
                onClick={() => onChangeGroups(false)}
                className={[
                  classes.pill,
                  !showGroups ? classes.pillOn : "",
                ].join(" ")}
              >
                {i18n.t("chatIndex.chats") || "Chats"}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={showGroups}
                onClick={() => onChangeGroups(true)}
                className={[
                  classes.pill,
                  showGroups ? classes.pillOn : "",
                ].join(" ")}
              >
                {i18n.t("chatIndex.groups") || "Grupos"}
              </button>
            </div>
          }
        />
      </div>
    );
  };

  const renderConversation = (onBack) => (
    <ChatMessages
      chat={currentChat}
      scrollToBottomRef={scrollToBottomRef}
      pageInfo={messagesPageInfo}
      messages={messages}
      loading={loading}
      loadingMore={loadingMore}
      handleSendMessage={sendMessage}
      handleLoadMore={loadMoreMessages}
      onEdit={handleEditMessage}
      onDelete={handleDeleteMessage}
      onForward={handleForwardMessage}
      justOpenedChat={justOpenedChat}
      setJustOpenedChat={setJustOpenedChat}
      messageListRef={messageListRef}
      addOptimisticMessage={addOptimisticMessage}
      onBack={onBack}
    />
  );

  const renderGrid = () => {
    return (
      <div className={classes.layout}>
        {renderListPane(showGroupChats, setShowGroupChats, false)}
        <div className={classes.messagePane}>
          {isObject(currentChat) && has(currentChat, "id") ? (
            renderConversation()
          ) : (
            <div className={classes.emptyConversation}>
              <ForumOutlinedIcon />
              <strong>Selecione uma conversa</strong>
              <span>
                Escolha um chat na lista ao lado para visualizar o histórico e
                enviar mensagens à sua equipe.
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Telas estreitas: ou a lista, ou a conversa (com a seta de voltar no cabeçalho).
  const renderTab = () => {
    return (
      <div className={classes.layout}>
        {!currentChat.id ? (
          renderListPane(tab === 1, (groups) => setTab(groups ? 1 : 0), true)
        ) : (
          <div className={classes.messagePane}>
            {renderConversation(() => setCurrentChat({}))}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (id && chats.length) {
      const chat = chats.find((r) => r.uuid === id);
      // Só chama selectChat se o chat atual for diferente
      if (chat && (!currentChat || currentChat.id !== chat.id)) {
        selectChat(
          chat,
          "useEffect[id, chats] (mudança de rota ou lista de chats)"
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, chats]);

  const addOptimisticMessage = (msg) => {
    setMessages((prev) => [...prev, msg]);
  };

  return (
    <>
      <ChatModal
        type={dialogType}
        open={showDialog}
        chat={currentChat}
        handleLoadNewChat={handleLoadNewChat}
        handleClose={() => setShowDialog(false)}
        findChats={findChats}
        setChats={setChats}
        setChatsPageInfo={setChatsPageInfo}
        chats={chats}
        loggedInUserId={user.id}
      />
      <div className={classes.mainContainer}>
        {isMdUp ? renderGrid() : renderTab()}
      </div>
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        classes={{
          paper: `${classes.dialogPaper} ${classes.dialogPaperWide}`,
        }}
        aria-labelledby="chat-edit-message-title"
      >
        <div className={classes.dialogHeader}>
          <h2 id="chat-edit-message-title" className={classes.dialogTitle}>
            Editar Mensagem
          </h2>
        </div>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.field}>
            <span className={classes.fieldLabel}>Mensagem</span>
            <TextField
              autoFocus
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              multiline
              minRows={3}
              maxRows={10}
              inputProps={{ "aria-label": "Mensagem" }}
            />
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => setEditModalOpen(false)}
            className={classes.dialogCancel}
          >
            {i18n.t("chatIndex.cancel")}
          </Button>
          <Button
            onClick={handleEditSubmit}
            color="primary"
            variant="contained"
            className={classes.dialogConfirm}
          >
            {i18n.t("chatIndex.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        classes={{ paper: classes.dialogPaper }}
        aria-labelledby="chat-delete-message-title"
      >
        <div className={classes.dialogHeader}>
          <h2 id="chat-delete-message-title" className={classes.dialogTitle}>
            {i18n.t("chatIndex.deleteGroup")}
          </h2>
        </div>
        <DialogContent className={classes.dialogContent}>
          <p className={classes.dialogText}>
            {i18n.t("chatIndex.deleteGroupConfirm")}
          </p>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            className={classes.dialogCancel}
          >
            {i18n.t("chatIndex.cancel")}
          </Button>
          <Button
            onClick={handleDeleteSubmit}
            variant="contained"
            className={classes.dialogDanger}
          >
            {i18n.t("chatIndex.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={forwardModalOpen}
        onClose={() => setForwardModalOpen(false)}
        classes={{ paper: classes.dialogPaper }}
        aria-labelledby="chat-forward-message-title"
      >
        <div className={classes.dialogHeader}>
          <h2 id="chat-forward-message-title" className={classes.dialogTitle}>
            Encaminhar Mensagem
          </h2>
        </div>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.field}>
            <span className={classes.fieldLabel}>Selecione o chat</span>
            <Select
              fullWidth
              displayEmpty
              variant="outlined"
              margin="dense"
              value={selectedChatForForward || ""}
              onChange={(e) => setSelectedChatForForward(e.target.value)}
              inputProps={{ "aria-label": "Selecione o chat" }}
              MenuProps={{
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null,
              }}
              renderValue={(value) => {
                const chat = chats.find((c) => c.id === value);
                if (!chat) {
                  return (
                    <span className={classes.placeholder}>Selecione o chat</span>
                  );
                }
                return chat.isGroup
                  ? chat.title
                  : chat.users.find((u) => u.userId !== user.id)?.user?.name;
              }}
            >
              {chats.map((chat) => (
                <MenuItem dense key={chat.id} value={chat.id}>
                  {chat.isGroup
                    ? chat.title
                    : chat.users.find((u) => u.userId !== user.id)?.user?.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => setForwardModalOpen(false)}
            className={classes.dialogCancel}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleForwardSubmit}
            color="primary"
            variant="contained"
            className={classes.dialogConfirm}
            disabled={!selectedChatForForward}
          >
            Encaminhar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default withWidth()(Chat);
